import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import SortArrowIcon from '../icons/SortArrowIcon';
import NamedBox from '../atoms/NamedBox';
import { useTableSort, SortDirection } from '../hooks/useTableSort';
import { CollegeProductionRow } from '../types/CollegeProductionData';
import { COLOR_BACKGROUND_WARM, COLOR_BACKGROUND_LIGHT, COLOR_MIDDLE_GRAY, COLOR_TEXT, COLOR_WHITE, COLOR_BLUE } from '../styles/colors';

interface PuntKickoffStatRow {
  year: number;
  teamName: string;
  punt: number;
  puntYard: number;
  puntAverage: number;
  kickoff: number;
  kickoffTouchback: number;
  touchbackPct: string;
  kickoffOutOfBounds: number;
}

type ColumnAlign = 'center' | 'left';

type ColumnKey = keyof CollegeProductionRow | 'puntAverage' | 'touchbackPct';

interface ColumnConfig {
  key: ColumnKey;
  label: string;
  align: ColumnAlign;
}

const COLUMNS: ColumnConfig[] = [
  { key: 'year', label: 'Year', align: 'center' },
  { key: 'teamName', label: 'Team', align: 'left' },
  { key: 'punt', label: 'Punts', align: 'center' },
  { key: 'puntYard', label: 'Punt Yards', align: 'center' },
  { key: 'puntAverage', label: 'Punt Average', align: 'center' },
  { key: 'kickoff', label: 'Kickoffs', align: 'center' },
  { key: 'kickoffTouchback', label: 'Touchbacks', align: 'center' },
  { key: 'touchbackPct', label: 'Touchback %', align: 'center' },
  { key: 'kickoffOutOfBounds', label: 'KO OOB', align: 'center' },
];

/**
 * Registry of derived columns. Each entry knows how to compute its raw numeric
 * value from a row (used for sorting) and how to format it for display.
 * Add a new computed column here without touching render/sort logic.
 */
const COMPUTED_COLUMNS: Record<string, {
  compute: (row: CollegeProductionRow) => number;
  format: (value: number) => string;
}> = {
  puntAverage: {
    compute: row => (row.punt ? row.puntYard / row.punt : 0),
    format: value => value.toFixed(1),
  },
  touchbackPct: {
    compute: row => (row.kickoff ? (row.kickoffTouchback / row.kickoff) * 100 : 0),
    format: value => `${value.toFixed(1)}%`,
  },
};

// const data: PuntKickoffStatRow[] = [
//   {
//     year: 2025,
//     teamName: 'Purdue',
//     punt: 50,
//     puntYard: 2126,
//     puntAverage: 43.2,
//     kickoff: 0,
//     kickoffTouchback: 0,
//     touchbackPct: '0.0%',
//     kickoffOutOfBounds: 0,
//   },
// ];

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  headerName: {
    fontWeight: 700,
  },
  tableWrapper: {
    width: '100%',
    overflowX: 'auto' as const,
    backgroundColor: '#ffffff',
  },
  table: {
    width: '100%',
    minWidth: 960,
    borderCollapse: 'collapse' as const,
    border: `1px solid ${COLOR_BACKGROUND_WARM}`,
    fontSize: theme.typography.pxToRem(16),
  },
  theadRow: {
    height: 40,
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e0e0e0',
  },
  th: {
    overflow: 'hidden',
    border: `1px solid ${COLOR_BACKGROUND_WARM}`,
    borderTop: `2px solid ${COLOR_MIDDLE_GRAY}`,
    padding: theme.spacing(1),
    whiteSpace: 'nowrap' as const,
    color: COLOR_TEXT,
    fontWeight: 700,
    textAlign: 'left' as const,
    userSelect: 'none' as const,
    cursor: 'pointer',

    '&:first-child': {
      width: 80,
      minWidth: 80,
    },

    '&:last-child': {
      borderRight: 'none',
    },
  },
  sortLabel: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(0.25),
  },
  sortIcon: {
    opacity: 1,
    color: COLOR_MIDDLE_GRAY,

    '& svg': {
      display: 'block',
      width: 9,
      height: 'auto',
    },
  },
  alignCenter: {
    textAlign: 'center' as const,
  },
  alignLeft: {
    textAlign: 'left' as const,
  },
  bodyRow: {
    height: 44,
    backgroundColor: COLOR_WHITE,

    '&:nth-child(odd)': {
      backgroundColor: COLOR_BACKGROUND_LIGHT,
    },
  },
  td: {
    fontWeight: 600,
    color: '#1e1e1e',
    padding: theme.spacing(0.75, 1),
    whiteSpace: 'nowrap' as const,
    borderRight: '1px solid #f0f0f0',

    '&:last-child': {
      borderRight: 'none',
    },
  },
}), { name: 'SpecialTeamYearlyPuntKickoffStatsTable' });

interface SpecialTeamYearlyPuntKickoffStatsTableProps {
  className?: string;
  data?: CollegeProductionRow[] | null;
}

export default function SpecialTeamYearlyPuntKickoffStatsTable(props: SpecialTeamYearlyPuntKickoffStatsTableProps) {
  const { className, data = [] } = props;
  const classes = useStyles();

  const getSortValue = (row: CollegeProductionRow, key: ColumnKey): number | string => {
    const computed = COMPUTED_COLUMNS[key as string];
    if (computed) {
      return computed.compute(row);
    }
    return row[key as keyof CollegeProductionRow] as number | string;
  };

  const { sortKey, sortDirection, handleSort, sortedRows: sortedData } = useTableSort<CollegeProductionRow, ColumnKey>({
    rows: data,
    initialSortKey: 'year',
    initialDirection: 'desc',
    nextKeyDirection: 'desc',
    sortRows: (rows, key, direction) => {
      rows.sort((first, second) => {
        const firstValue = getSortValue(first, key);
        const secondValue = getSortValue(second, key);

        let comparison = 0;
        if (typeof firstValue === 'number' && typeof secondValue === 'number') {
          comparison = firstValue - secondValue;
        } else {
          comparison = String(firstValue).localeCompare(String(secondValue));
        }

        return direction === 'asc' ? comparison : -comparison;
      });

      return rows;
    },
  });

  return (
    <NamedBox
      className={clsx(classes.root, className)}
      headerNameClassName={classes.headerName}
      name="Yearly Punt & Kickoff Stats"
    >
      <div className={classes.tableWrapper}>
        <table className={classes.table}>
          <thead>
            <tr className={classes.theadRow}>
              {COLUMNS.map(column => {
                const isActive = column.key === sortKey;

                return (
                  <th
                    key={column.key}
                    className={clsx(classes.th)}
                  >
                    <TableSortLabel
                      active={isActive}
                      direction={isActive ? sortDirection : 'asc'}
                      className={classes.sortLabel}
                      hideSortIcon={false}
                      IconComponent={(iconProps) => (
                        <SortArrowIcon
                          {...iconProps}
                          className={`${iconProps.className} ${classes.sortIcon}`}
                          style={{
                            color: isActive ? COLOR_BLUE : '#b5b5b5',
                          }}
                        />
                      )}
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedData.map(row => (
              <tr key={row.year} className={classes.bodyRow}>
                {COLUMNS.map(column => {
                  const alignClass = column.align === 'left' ? classes.alignLeft : classes.alignCenter;
                  const computed = COMPUTED_COLUMNS[column.key as string];
                  const cellValue = computed
                    ? computed.format(computed.compute(row))
                    : row[column.key as keyof CollegeProductionRow];
                  return (
                    <td key={column.key} className={clsx(classes.td, alignClass)}>
                      {cellValue}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </NamedBox>
  );
}
